import { api } from '@/Data/api';
import ss from '../Session';

export interface ImageUploadResponse {
    uploadURL: string;
    Key: string;
}

export interface ImageMetadata {
    ContentLength: number;
    ContentType: string;
    ETag: string;
    LastModified: string;
}

export interface ImageResponse<T = any> {
    code: number;
    data?: T;
    message?: string;
}

/**
 * Generate pre-signed S3 URL for image upload
 * @param filename Name of the file to upload
 * @returns Promise with upload URL and S3 key
 */
export const generateUploadURL = async (filename: string): Promise<ImageUploadResponse | null> => {
    try {
        const res = await api.put<ImageResponse<ImageUploadResponse>>('/image', 
            { filename },
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`
                }
            }
        );
        
        return res.data?.code === 200 ? res.data.data ??null : null;
    } catch (e) {
        console.error('Failed to generate upload URL:', e);
        return null;
    }
};

/**
 * Upload image to S3 using pre-signed URL
 * @param imageUri Local image URI to upload
 * @param filename Name for the uploaded file
 * @returns Promise with S3 key on success, null on failure
 */
export const uploadImageToS3 = async (imageUri: string, filename?: string): Promise<string | null> => {
    try {
        // Extract filename from URI if not provided
        const finalFilename = filename || imageUri.split('/').pop() || `image_${Date.now()}.jpg`;
        
        // Get upload URL from Image API
        const uploadData = await generateUploadURL(finalFilename);
        if (!uploadData) {
            throw new Error('Failed to get upload URL');
        }
        
        const { uploadURL, Key } = uploadData;
        
        // Create file blob for upload
        const response = await fetch(imageUri);
        const blob = await response.blob();
        
        // Upload image to S3
        const uploadResult = await fetch(uploadURL, {
            method: 'PUT',
            body: blob,
            headers: {
                'Content-Type': 'image/jpeg'
            }
        });
        
        if (uploadResult.ok) {
            return 'https://static.koreanmate.app/'+Key;
        } else {
            throw new Error('Failed to upload to S3');
        }
    } catch (error) {
        console.error('Image upload error:', error);
        return null;
    }
};

/**
 * Get image metadata from S3
 * @param key S3 key of the image
 * @returns Promise with image metadata
 */
export const getImageMetadata = async (key: string): Promise<ImageMetadata | null> => {
    try {
        const res = await api.get<ImageResponse<ImageMetadata>>('/image', {
            params: { key },
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? res.data.data??null : null;
    } catch (e) {
        console.error('Failed to get image metadata:', e);
        return null;
    }
};

/**
 * Get public URL for an image stored in S3
 * @param key S3 key of the image
 * @returns Public URL for the image
 */
export const getImageURL = (key: string): string => {
    return `https://static.koreanmate.app/${key}`;
};

/**
 * Upload multiple images to S3
 * @param imageUris Array of local image URIs to upload
 * @returns Promise with array of S3 keys for successfully uploaded images
 */
export const uploadMultipleImages = async (imageUris: string[]): Promise<string[]> => {
    const uploadPromises = imageUris.map(uri => uploadImageToS3(uri));
    const results = await Promise.allSettled(uploadPromises);
    
    return results
        .filter((result): result is PromiseFulfilledResult<string> => 
            result.status === 'fulfilled' && result.value !== null
        )
        .map(result => result.value);
};